import { DrugProductLabel } from "../models/types";

/**
 * 허가사항 모듈(대분류) 코드 → 한글명.
 * (DrugLabelInfo에서 이관 — ReferenceFilterBar 코드 필터와 공유하기 위해 공개 상수로 추출)
 */
export const MODULE_NAME_MAP: Record<string, string> = {
  INGREDI: "성분",
  EE: "효능효과",
  UD: "용법용량",
  NB: "사용상의 주의사항",
  ACTN: "약리작용",
  EXPR: "전문가를 위한 정보",
  STOR: "보관 및 취급상의 주의",
  ELSE: "기타",
  UNDEF: "미분류",
  META: "기타정보",
  BASIC: "기본정보",
};

/**
 * 서브모듈(주로 NB 하위) 코드 → 한글명. 탭 무관 공통.
 */
export const SUBMODULE_NAME_MAP: Record<string, string> = {
  WARN1: "경고",
  CONT2: "다음 환자에는 투여하지 말 것",
  CARE3: "다음 환자에는 신중히 투여할 것",
  SIDE4: "이상반응",
  CARE5: "일반적 주의",
  PRCU6: "임부에 대한 투여",
  PRCU7: "소아에 대한 투여",
  PRCU8: "고령자에 대한 투여",
  OVER9: "과량투여시의 처치",
  ADMN10: "적용상의 주의",
  WARN: "경고",
  CONT: "금기",
  CARE: "신중투여",
  SIDE: "이상반응",
  DDIN: "상호작용",
  ADMN: "적용상의 주의",
  ACTN: "약리작용",
  OVER: "과량투여",
  CLIX: "임상검사치에의 영향",
  STOR: "보관 및 취급상의 주의",
  // META(기타정보) 하위 — 백엔드 crawling.service fieldMap 한글 키와 일치
  PACK: "포장정보",
  EXPD: "사용기간",
  RMP: "RMP대상",
  // BASIC(기본정보) 하위 — 위와 동일 정본
  IDMK: "기타식별표시",
  CMFR: "위탁제조업체",
  ELSE: "기타",
  EXPR: "전문가를 위한 정보",
  KDNY: "신장애 환자",
  LIVR: "간장애 환자",
  PRD: "성분 및 함량",
  PRCU: "임부/수유부/소아/고령자",
  UNDEF: "미분류",
  GNRL: "일반적 주의사항",
  LIKI: "신장애 및 간장애 환자 주의사항",
  SECTION: "섹션",
};

/** 모듈 표시 자연 순서. NB는 하위(submodule)로 평탄화돼 이 자리에 들어간다.
 *  META(기타정보)·BASIC(기본정보)는 크롤 데이터 자연 순서상 말미에 오며, BASIC은 META 바로 뒤. */
const MODULE_ORDER = [
  "INGREDI",
  "EE",
  "UD",
  "NB",
  "ACTN",
  "STOR",
  "EXPR",
  "ELSE",
  "UNDEF",
  "META",
  "BASIC",
];

/** NB 하위 서브모듈 자연 순서(허가사항 문서 흐름). 목록에 없으면 뒤로. */
export const SUBMODULE_ORDER = [
  "WARN",
  "CONT",
  "CARE",
  "SIDE",
  "DDIN",
  "ADMN",
  "OVER",
  "CLIX",
  "KDNY",
  "LIVR",
  "LIKI",
  "PRCU",
  "GNRL",
  "PRD",
  "ACTN",
  "STOR",
  "PACK",
  "EXPD",
  "RMP",
  "IDMK",
  "CMFR",
  "EXPR",
  "ELSE",
  "SECTION",
  "UNDEF",
];

/** 숫자 접미(STOR6, CONT2 등) 제거한 기본 코드. */
export function baseCode(code: string): string {
  const match = (code || "").match(/^([A-Z]+)/);
  return match ? match[1] : code || "";
}

/** 모듈 코드 → 한글명. moduleNameMap(탭별: 건기식 등) 우선, 없으면 공통 MODULE_NAME_MAP. */
export function moduleLabel(code: string, moduleNameMap?: Record<string, string>): string {
  return (
    moduleNameMap?.[code] ||
    MODULE_NAME_MAP[code] ||
    moduleNameMap?.[baseCode(code)] ||
    MODULE_NAME_MAP[baseCode(code)] ||
    baseCode(code) ||
    code
  );
}

/** 서브모듈 코드 → 한글명(숫자 변형 정규화 포함). */
export function submoduleLabel(code: string): string {
  return (
    SUBMODULE_NAME_MAP[code] || SUBMODULE_NAME_MAP[baseCode(code)] || baseCode(code) || code
  );
}

/** 코드 필터 옵션 1개 — 한 한글 라벨 = 그 라벨에 매핑되는 실제 코드 집합 */
export interface CodeFilterOption {
  label: string;
  codes: string[];
}

/**
 * corpus(productLabels 목록) → 코드 필터 옵션(한글 라벨 단위).
 * - NB module은 상위 라벨로 노출하지 않고 그 하위 submodule을 개별 라벨로 평탄화.
 * - 그 외 module은 module 라벨로.
 * - 같은 한글 라벨(예: STOR이 module/submodule 양쪽, CONT/CONT2)은 한 옵션으로 묶고 codes를 union.
 * - 정렬: 허가사항 자연 순서(MODULE_ORDER, NB 자리엔 SUBMODULE_ORDER).
 * 순수함수 → 단위 테스트 대상.
 */
export function buildCodeFilterOptions(
  labels: DrugProductLabel[] | undefined,
  moduleNameMap?: Record<string, string>
): CodeFilterOption[] {
  if (!labels || labels.length === 0) return [];

  // label → { codes:Set, group, sub } (group/sub는 최소값으로 정렬 안정화)
  const map = new Map<string, { codes: Set<string>; group: number; sub: number }>();

  for (const item of labels) {
    const isNB = item.module === "NB";
    // NB 항목에 submodule이 없으면 "미분류"(UNDEF)로 폴백(DrugLabelInfo 필터와 동일 규칙).
    const code = isNB ? item.submodule || "UNDEF" : item.module || "";
    if (!code) continue;
    const label = isNB ? submoduleLabel(code) : moduleLabel(code, moduleNameMap);

    // 정렬 키: group = 모듈 순서, sub = (NB면 submodule 순서)
    const moduleIdx = MODULE_ORDER.indexOf(isNB ? "NB" : baseCode(item.module || ""));
    const group = moduleIdx === -1 ? MODULE_ORDER.length : moduleIdx;
    const subIdx = isNB ? SUBMODULE_ORDER.indexOf(baseCode(code)) : -1;
    const sub = subIdx === -1 ? (isNB ? SUBMODULE_ORDER.length : -1) : subIdx;

    const prev = map.get(label);
    if (prev) {
      prev.codes.add(code);
      prev.group = Math.min(prev.group, group);
      prev.sub = Math.min(prev.sub, sub);
    } else {
      map.set(label, { codes: new Set([code]), group, sub });
    }
  }

  return Array.from(map.entries())
    .sort((a, b) => a[1].group - b[1].group || a[1].sub - b[1].sub || a[0].localeCompare(b[0], "ko"))
    .map(([label, v]) => ({ label, codes: Array.from(v.codes) }));
}

/** 약 내부 목차/점프용 id 헬퍼 — itemCode 포함이라 전역 유일.
 *  접두사 "sec-"/"sub-"는 문자 시작 보장용 — itemCode가 숫자로 시작해도
 *  `querySelector("#...")`가 유효하도록(숫자 시작 id 선택자는 무효). */
export function sectionDomId(itemCode: string, moduleCode: string): string {
  return `sec-${itemCode}-${moduleCode}`;
}
export function subSectionDomId(itemCode: string, submodule: string): string {
  return `sub-${itemCode}-${baseCode(submodule)}`;
}

/** 약 내부 목차 항목 — 모듈(depth 0) / NB 하위 소제목(depth 1) */
export interface SectionTocItem {
  label: string;
  targetId: string;
  depth: 0 | 1;
}

/**
 * 약 productLabels → 약 내부 목차(2계층). 모듈은 자연순(MODULE_ORDER), NB는 하위 submodule을
 * SUBMODULE_ORDER 자연순으로 들여쓰기(depth 1). selectedCodes로 코드 필터 반영(미전달=전체).
 * 본문 렌더(renderCautionsBySubmodule)와 동일 순서·동일 id를 사용해 정합. 순수함수 → 단위 테스트.
 */
export function buildSectionToc(
  itemCode: string,
  labels: DrugProductLabel[] | undefined,
  selectedCodes: string[] | null | undefined,
  moduleNameMap?: Record<string, string>
): SectionTocItem[] {
  if (!labels || labels.length === 0) return [];
  const codeSet = selectedCodes ? new Set(selectedCodes) : null;

  // 코드 필터 반영(DrugLabelInfo.filteredGroupedLabels와 동일 규칙)
  const kept = labels.filter((item) => {
    if (!codeSet) return true;
    const code = item.module === "NB" ? item.submodule || "UNDEF" : item.module || "";
    return codeSet.has(code);
  });
  if (kept.length === 0) return [];

  // 모듈별 존재 여부 + NB 하위 submodule 집합
  const modules = new Set<string>();
  const nbSubs = new Set<string>();
  for (const item of kept) {
    const mod = item.module || "";
    if (!mod) continue;
    modules.add(mod);
    if (mod === "NB") nbSubs.add(item.submodule || "UNDEF");
  }

  const moduleSort = (m: string) => {
    const i = MODULE_ORDER.indexOf(baseCode(m));
    return i === -1 ? MODULE_ORDER.length : i;
  };
  const subSort = (s: string) => {
    const i = SUBMODULE_ORDER.indexOf(baseCode(s));
    return i === -1 ? SUBMODULE_ORDER.length : i;
  };

  const out: SectionTocItem[] = [];
  for (const mod of Array.from(modules).sort((a, b) => moduleSort(a) - moduleSort(b))) {
    out.push({
      label: moduleLabel(mod, moduleNameMap),
      targetId: sectionDomId(itemCode, mod),
      depth: 0,
    });
    if (mod === "NB") {
      // 같은 한글 라벨(숫자 변형)은 한 번만 — baseCode 기준 dedup, 자연순
      const seen = new Set<string>();
      for (const sub of Array.from(nbSubs).sort((a, b) => subSort(a) - subSort(b))) {
        const id = subSectionDomId(itemCode, sub);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ label: submoduleLabel(sub), targetId: id, depth: 1 });
      }
    }
  }
  return out;
}
